import React, { useState, useEffect } from 'react';
import { generateAssessmentQuiz } from '../services/geminiService';
import { AssessmentResponse } from '../types';
import { useUser } from '../context/UserContext';
import { Brain, CheckCircle, Trophy, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react';

const Assessment: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [quiz, setQuiz] = useState<AssessmentResponse | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState<number[]>([]);
  const [finished, setFinished] = useState(false);
  const { addXp, incrementStat } = useUser();
  const [error, setError] = useState<string | null>(null);

  const fetchQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await generateAssessmentQuiz();
      if(data && data.questions) {
        setQuiz(data);
      } else {
        throw new Error("Invalid format");
      }
    } catch (err) {
      setError("Could not generate assessment.");
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = () => {
    setScores([]);
    setCurrentQuestion(0);
    setFinished(false);
    fetchQuiz();
  };

  const handleAnswer = (score: number) => {
    const newScores = [...scores, score];
    setScores(newScores);
    
    if (quiz && currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      finishQuiz(newScores);
    }
  };

  const finishQuiz = (finalScores: number[]) => {
    setFinished(true);
    incrementStat('assessmentsCompleted');
    const totalScore = finalScores.reduce((a, b) => a + b, 0);
    addXp(totalScore * 5, "Assessment Completed"); // Bonus XP based on performance
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nunchi-700 mb-4"></div>
        <p className="text-nunchi-600">Preparing your assessment...</p>
      </div>
    );
  }

  if (error) {
     return (
       <div className="text-center py-20">
         <AlertTriangle className="mx-auto text-red-400 mb-4" size={48} />
         <h2 className="text-xl font-bold mb-2">Error</h2>
         <p className="mb-4">{error}</p>
         <button onClick={startQuiz} className="bg-nunchi-700 text-white px-6 py-2 rounded-lg">Try Again</button>
       </div>
     );
  }

  if (!quiz && !finished) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-10 text-center">
        <div className="w-20 h-20 bg-nunchi-100 rounded-full flex items-center justify-center mx-auto mb-6 text-nunchi-600">
          <Brain size={40} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-4">Nunchi Skill Assessment</h1>
        <p className="text-lg text-nunchi-600 mb-8">
          Test your ability to read the room with 5 situational questions. 
          Your choices will determine your Nunchi score and archetype.
        </p>
        <button 
          onClick={startQuiz}
          className="bg-nunchi-700 text-white px-8 py-3 rounded-xl font-bold hover:bg-nunchi-800 transition-all shadow-lg flex items-center gap-2 mx-auto"
        >
          Begin Assessment <ArrowRight size={20} />
        </button>
      </div>
    );
  }

  if (finished) {
    const totalScore = scores.reduce((a, b) => a + b, 0);
    const maxScore = scores.length * 10;
    const percentage = Math.round((totalScore / maxScore) * 100);
    
    let archetype = "";
    let description = "";
    
    if (percentage >= 90) {
      archetype = "The Nunchi Master";
      description = "You can read the air before it even enters the room. You navigate social situations with flawless grace.";
    } else if (percentage >= 70) {
      archetype = "The Mediator";
      description = "You have strong instincts and rarely cause offense. You are a valued presence in any group.";
    } else if (percentage >= 40) {
      archetype = "The Observer";
      description = "You are learning to see beyond yourself. Keep practicing to react faster to changing moods.";
    } else {
      archetype = "The Bulldozer";
      description = "You often miss subtle cues. Remember: Listen twice as much as you speak.";
    }

    return (
      <div className="max-w-2xl mx-auto px-6 py-10 text-center animate-fadeIn">
        <Trophy className="mx-auto text-amber-500 mb-6" size={64} />
        <h2 className="text-xl font-bold text-nunchi-500 uppercase tracking-wider mb-2">Assessment Complete</h2>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-4">{percentage}% Score</h1>
        
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-nunchi-200 mb-8">
          <h3 className="text-2xl font-bold text-nunchi-800 mb-2">{archetype}</h3>
          <p className="text-nunchi-600 leading-relaxed">{description}</p>
        </div>

        <button 
          onClick={startQuiz}
          className="text-nunchi-600 hover:text-nunchi-900 font-bold flex items-center justify-center gap-2 mx-auto"
        >
          <RefreshCw size={16} /> Retake Assessment
        </button>
      </div>
    );
  }

  const question = quiz!.questions[currentQuestion];

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20">
      <div className="mb-8">
        <div className="flex justify-between text-xs font-bold text-nunchi-400 uppercase tracking-widest mb-2">
          <span>Question {currentQuestion + 1} of {quiz!.questions.length}</span>
          <span>Assessment</span>
        </div>
        <div className="w-full h-2 bg-nunchi-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-nunchi-600 transition-all duration-500"
            style={{ width: `${((currentQuestion + 1) / quiz!.questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200 mb-6">
        <h2 className="text-xl font-serif font-bold text-nunchi-900 mb-4">{question.scenario}</h2>
      </div>

      <div className="space-y-3">
        {question.options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(option.score)}
            className="w-full text-left p-4 rounded-xl border border-nunchi-200 hover:border-nunchi-400 hover:bg-nunchi-50 transition-all flex items-center gap-3 group"
          >
            <div className="w-8 h-8 rounded-full bg-white border border-nunchi-300 flex items-center justify-center text-nunchi-500 group-hover:bg-nunchi-200 group-hover:text-nunchi-700 font-bold text-sm">
              {String.fromCharCode(65 + idx)}
            </div>
            <span className="text-nunchi-800">{option.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Assessment;